import { useMatomo } from '@datapunt/matomo-tracker-react';
import DateFnsUtils from '@date-io/date-fns';
import { Box, Card, CardContent, CircularProgress, Grid, TextField } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import Switch from '@material-ui/core/Switch';
import Typography from '@material-ui/core/Typography';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import Alert from '@material-ui/lab/Alert';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { Auth } from 'aws-amplify';
import { DateTime } from 'luxon';
import React, { Fragment, useEffect, useState } from 'react';
import InputMask from 'react-input-mask';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Select, { createFilter } from 'react-select';
import counties from 'src/assets/data/counties.json';
import { EnumHasScholarship, EnumProfileRelation, EnumProfileType, MAppMeta, MProfile } from 'src/private/redux/scholarship.models';

import { deleteProfileAsync, saveProfileAsync, saveProfileFailure } from '../../redux/scholarship.actions';
import {
  selectApplicationId,
  selectApplicationsInProgress,
  selectError,
  selectIsSavingProfile,
  selectProfile,
  selectProfileAny,
  selectStepsCompleted
} from '../../redux/scholarship.selectors';
import { createNewProfile } from '../../redux/scholarship.utils';
import { ApplicationFormContainer } from '../ApplicationsForm/applications-form.styles';

const useStyles = makeStyles((theme) =>
  createStyles({
    root: {
      width: '100%',
      textAlign: 'center'
    },
    rootLeft: {
      width: '100%',
      textAlign: 'right',
      marginTop: '40px'
    },
    button: {
      marginRight: theme.spacing(1),
      height: '45px'
    },
    cardTop: {
      marginTop: theme.spacing(3)
    },
    buttonSmall: {
      marginRight: theme.spacing(1),
      height: '25px',
      font: '15px;',
      backgroundColor: '#f87c7c'
    },
    hint: {
      font: '0.8rem',
      color: '#5057cb',
      clear: 'left'
    },
    customLabelStyling: {
      color: 'rgba(0, 0, 0, 0.54)',
      'font-size': '0.8rem'
    },
    lastGradeDot: {
      width: 4,
      height: 4,
      borderRadius: '50%',
      backgroundColor: theme.palette.grey[500],
      position: 'relative',
      right: '1.45rem',
      bottom: '.2rem',
      display: 'inline-block'
    }
  })
);

/* eslint-disable max-lines */
// eslint-disable-next-line complexity
const ApplicationsFormEditProfile = ({
  dispatchProfileSave,
  dispatchProfileDelete
}: {
  dispatchProfileSave: (value: MProfile, progress: unknown) => Promise<unknown>;
  dispatchProfileDelete: (value: MProfile) => Promise<unknown>;
}) => {
  const { profileId: profileIdParam, type: profileTypeParam } = useParams();
  const id = profileIdParam ?? 'new';
  const profileType = (profileTypeParam as EnumProfileType | undefined) ?? EnumProfileType.PARENT;
  const navigate = useNavigate();
  const location = useLocation();
  const existingProfile = useSelector(selectProfile(id) as (state: unknown) => MProfile | undefined);
  const anyProfile = useSelector(selectProfileAny as (state: unknown) => MProfile | undefined);
  const applicationsInProgress = useSelector(selectApplicationsInProgress as (state: unknown) => MAppMeta[]);
  const isSaving = useSelector(selectIsSavingProfile as (state: unknown) => boolean);
  const applicationId = useSelector(selectApplicationId);
  const error = useSelector(selectError as (state: unknown) => string | null);
  const completed = useSelector(selectStepsCompleted);
  const fromProfilesList = location.state?.fromProfilesList;

  const [open, setOpen] = useState(false);
  const [confirmation, setConfirmation] = useState<string | null>(null);
  const [phone, setPhone] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [profile, setProfile] = useState<MProfile>(existingProfile ?? createNewProfile(anyProfile, profileType));
  const [lastGrade, _] = useState(profile?.grade);
  const [blockNavigation, setBlockNavigation] = useState(false);
  const [countyValueObj, setCountyValueObj] = useState<{ value: string | null; label: string | null }>({
    value: profile?.addressCounty,
    label: profile?.addressCounty
  });
  const { trackPageView } = useMatomo();

  // Track page view
  const dispatch = useDispatch();
  useEffect(() => {
    trackPageView({});

    return () => {
      dispatch(saveProfileFailure(null));
    };
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (blockNavigation) {
        event.preventDefault();
        event.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [blockNavigation]);

  const disableNames = (): boolean => {
    if (profileType === EnumProfileType.CHILD) {
      return false;
    }
    if (profile.id === 'new') {
      return false;
    } else {
      return applicationsInProgress.length !== 0;
    }
  };

  const title = `${id === 'new' ? 'New' : 'Edit'} ${profileType === EnumProfileType.PARENT ? 'Parent' : 'Student'} Profile`;

  useEffect(() => {
    // unset grade if this profile is out of date, to force grade review
    if (Number(profile?.lastUpdated) !== DateTime.now().setZone('America/Chicago').year) {
      setProfile((prevState) => ({
        ...prevState,
        grade: 0
      }));
    }
  }, []);

  const handleChange = (e) => {
    let { name, value } = e.target;
    if (name === 'grade') {
      value = +value;
    }
    setProfile((prevState) => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    setProfile((prevState) => ({
      ...prevState,
      [name]: checked
    }));
  };

  const handleCountyChange = (e) => {
    if (e === null || e.value === '') {
      setProfile((prevState) => ({
        ...prevState,
        ['addressCounty']: ''
      }));
      setCountyValueObj({ value: null, label: null });
    } else {
      setProfile((prevState) => ({
        ...prevState,
        ['addressCounty']: e.value
      }));
      setCountyValueObj({
        value: e.value,
        label: e.value
      });
    }
  };

  useEffect(() => {
    if (id === 'new' && !profile) {
      const newProfile = createNewProfile(anyProfile, profileType);
      setProfile(newProfile);
      setBlockNavigation(true);
      handleCountyChange({ value: newProfile.addressCounty, label: newProfile.addressCounty });
    }
  }, []);

  useEffect(() => {
    if (profile?.addressCounty === '') {
      setCountyValueObj({ value: null, label: null });
    }
  }, []);

  useEffect(() => {
    const getUserInfo = async () => {
      const userInfo = await Auth.currentUserInfo();
      setPhone(userInfo.attributes['phone_number'].substring(2));
      setEmail(userInfo.attributes['email']);
      handleChange({ target: { name: 'phone', value: userInfo.attributes['phone_number'].substring(2) } });
      handleChange({ target: { name: 'email', value: userInfo.attributes['email'] } });
    };

    if (id === 'new' && profileType === EnumProfileType.PARENT && profile) {
      if (!profile.phone || !profile.email) {
        void getUserInfo();
      }
    }
  }, [profile]);

  const handleSubmit = (): void => {
    setBlockNavigation(false);
    void dispatchProfileSave(profile, completed)
      .then((result) => {
        if (result) {
          if (fromProfilesList) {
            navigate('/applications');
          } else {
            navigate(`/applications/${String(applicationId)}`);
          }
        }
      })
      .catch((err) => {
        console.error('Failed to save profile:', err);
      });
  };

  const handleCancel = () => {
    if (fromProfilesList) {
      navigate('/applications');
    } else {
      navigate(`/applications/${String(applicationId)}`);
    }
  };

  const handleDateChange = async (newDate) => {
    setProfile((prevState) => ({
      ...prevState,
      dob: newDate
    }));
  };

  const handleDelete = () => {
    setOpen(true);
  };

  const handleConfirmType = (e) => {
    setConfirmation(e.target.value.toLowerCase());
  };

  const handleConfirmDelete = (): void => {
    setOpen(false);

    if (confirmation === 'delete') {
      void dispatchProfileDelete(profile)
        .then((result) => {
          if (result) {
            if (fromProfilesList || !applicationId) {
              navigate('/applications');
            } else {
              navigate(`/applications/${String(applicationId)}`);
            }
          }
        })
        .catch((err) => {
          console.error('Failed to delete profile:', err);
        });
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const beforeMaskedValueChange = (newState, oldState, userInput) => {
    let { value } = newState;
    let selection = newState.selection;
    let cursorPosition = selection ? selection.start : null;

    // keep minus if entered by user
    if (value.trim().endsWith('-') && userInput !== '-' && !profile.addressZip.endsWith('-')) {
      if (cursorPosition === value.length) {
        cursorPosition--;
        selection = { start: cursorPosition, end: cursorPosition };
      }
      value = value.slice(0, -1);
    }

    return {
      value,
      selection
    };
  };

  const customStyles: Record<string, (provided: Record<string, unknown>) => Record<string, unknown>> = {
    control: (provided: Record<string, unknown>): Record<string, unknown> => ({
      ...provided,
      borderColor: '#949494'
    }),
    dropdownIndicator: (provided: Record<string, unknown>): Record<string, unknown> => ({
      ...provided,
      '&:hover': {
        color: 'lightgrey'
      },
      color: '#949494'
    }),
    clearIndicator: (provided: Record<string, unknown>): Record<string, unknown> => ({
      ...provided,
      '&:hover': {
        color: 'lightgrey'
      },
      color: '#949494'
    })
  };

  const classes = useStyles();
  if (profile) {
    return (
      <ApplicationFormContainer>
        <Grid container>
          <Grid item>
            <Typography variant="h4">{title}</Typography>
          </Grid>
        </Grid>

        <Card className={classes.cardTop}>
          <CardContent>
            <Box ml={4} mr={4} mb={3}>
              <Fragment>
                {error && <Alert severity="error">{error}</Alert>}
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      required
                      id="firstName"
                      name="firstName"
                      label="First name"
                      value={profile.firstName}
                      onChange={handleChange}
                      disabled={disableNames()}
                      fullWidth
                      autoComplete="given-name"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      required
                      id="lastName"
                      name="lastName"
                      label="Last name"
                      fullWidth
                      autoComplete="family-name"
                      value={profile.lastName}
                      disabled={disableNames()}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      required
                      id="address1"
                      name="address1"
                      label="Address line 1"
                      fullWidth
                      autoComplete="shipping address-line1"
                      value={profile.address1}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      id="address2"
                      name="address2"
                      label="Address line 2"
                      fullWidth
                      autoComplete="shipping address-line2"
                      value={profile.address2}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      required
                      id="city"
                      name="addressCity"
                      label="City"
                      fullWidth
                      autoComplete="shipping address-level2"
                      value={profile.addressCity}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3} style={{ paddingTop: '3px' }}>
                    <span className={classes.customLabelStyling}>County *</span>
                    <Select
                      value={countyValueObj}
                      styles={customStyles}
                      id="county"
                      name="addressCounty"
                      options={counties}
                      isSearchable={true}
                      isClearable={true}
                      openMenuOnFocus={true}
                      openMenuOnClick={true}
                      onChange={handleCountyChange}
                      filterOption={createFilter({
                        ignoreCase: true,
                        matchFrom: 'start'
                      })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <TextField
                      required
                      id="addressState"
                      name="addressState"
                      label="State"
                      fullWidth
                      value={profile.addressState}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <InputMask
                      mask="99999-9999"
                      value={profile.addressZip}
                      disabled={false}
                      maskChar={null}
                      onChange={handleChange}
                      beforeMaskedValueChange={beforeMaskedValueChange}
                    >
                      {() => (
                        <TextField name="addressZip" required id="addressZip" label="Zip" fullWidth autoComplete="shipping postal-code" />
                      )}
                    </InputMask>
                  </Grid>
                  {profileType === EnumProfileType.CHILD && (
                    <Grid item xs={12} sm={4}>
                      <MuiPickersUtilsProvider utils={DateFnsUtils}>
                        <KeyboardDatePicker
                          required
                          format="MM/dd/yyyy"
                          margin="normal"
                          id="dob"
                          name="dob"
                          label="Date of Birth"
                          value={profile.dob}
                          onChange={handleDateChange}
                          KeyboardButtonProps={{
                            'aria-label': 'change date'
                          }}
                        />
                        <br />
                        <Typography variant="inherit" className={classes.hint}>
                          Type in numbers or click icon for picker.
                        </Typography>
                      </MuiPickersUtilsProvider>
                    </Grid>
                  )}
                  {profileType === EnumProfileType.CHILD && (
                    <Grid item xs={12} sm={6}>
                      <span className={classes.customLabelStyling}>
                        Grade *
                        {!!lastGrade && profile.grade === 0 && <strong style={{ color: 'red' }}> Please Update Student's Grade.</strong>}
                      </span>
                      <RadioGroup aria-label="grade" name="grade" value={profile.grade} onChange={handleChange} row>
                        {[3, 4, 5, 6, 7, 8, 9].map((item: number) => (
                          <FormControlLabel
                            key={`grade_${item}`}
                            value={item}
                            control={<Radio />}
                            label={
                              <div>
                                {/* lastGrade === item && profile.grade === 0 && <div className={classes.lastGradeDot} />*/}
                                {item}
                              </div>
                            }
                          />
                        ))}
                      </RadioGroup>
                    </Grid>
                  )}
                  {profileType === EnumProfileType.CHILD && (
                    <Grid item xs={12} sm={12}>
                      Has your child previously received a Match the Promise scholarship or grant? (This is for informational purposes
                      only.)
                      <RadioGroup
                        aria-label="hasScholarship"
                        name="hasScholarship"
                        value={profile.hasScholarship}
                        onChange={handleChange}
                        row
                      >
                        <FormControlLabel control={<Radio />} label="Yes" value={EnumHasScholarship.YES} />
                        <FormControlLabel control={<Radio />} label="No" value={EnumHasScholarship.NO} />
                      </RadioGroup>
                    </Grid>
                  )}
                  {profileType === EnumProfileType.PARENT && (
                    <Grid item xs={12} sm={6}>
                      <RadioGroup aria-label="relationship" name="relationship" value={profile.relationship} onChange={handleChange} row>
                        {[EnumProfileRelation.FATHER, EnumProfileRelation.MOTHER, EnumProfileRelation.OTHER].map((item: string) => (
                          <FormControlLabel key={item} value={item} control={<Radio />} label={`${item}`} />
                        ))}
                      </RadioGroup>
                    </Grid>
                  )}
                  {profileType === EnumProfileType.PARENT && (profile.email || email) && (
                    <Grid item xs={12} sm={5}>
                      <TextField
                        required
                        id="email"
                        name="email"
                        label="Email"
                        fullWidth
                        autoComplete="email"
                        value={profile.email || email}
                        onChange={handleChange}
                      />
                    </Grid>
                  )}
                  {profileType === EnumProfileType.PARENT && (profile.phone || phone) && (
                    <Grid item xs={12} sm={3}>
                      <InputMask mask="999-999-9999" value={profile.phone || phone} disabled={false} maskChar=" " onChange={handleChange}>
                        {() => <TextField name="phone" required id="phone" label="Phone" fullWidth autoComplete="phone" />}
                      </InputMask>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={profile.hasMailingAddress || false}
                          value={profile.hasMailingAddress || false}
                          onChange={handleSwitchChange}
                          name="hasMailingAddress"
                        />
                      }
                      label={`Mailing address ${profile.hasMailingAddress ? 'different than' : 'same as'} above. *`}
                    />
                  </Grid>
                  {profile.hasMailingAddress && (
                    <>
                      <Grid item xs={12}>
                        <TextField
                          required
                          id="mailing1"
                          name="mailing1"
                          label="Address line 1"
                          fullWidth
                          value={profile.mailing1}
                          onChange={handleChange}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          id="mailing2"
                          name="mailing2"
                          label="Address line 2"
                          fullWidth
                          value={profile.mailing2}
                          onChange={handleChange}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          required
                          id="mailingCity"
                          name="mailingCity"
                          label="City"
                          fullWidth
                          value={profile.mailingCity}
                          onChange={handleChange}
                        />
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <TextField
                          id="mailingState"
                          name="mailingState"
                          label="State"
                          fullWidth
                          value={profile.mailingState}
                          onChange={handleChange}
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <InputMask
                          mask="99999-9999"
                          value={profile.mailingZip}
                          disabled={false}
                          maskChar={null}
                          onChange={handleChange}
                          beforeMaskedValueChange={beforeMaskedValueChange}
                        >
                          {() => <TextField name="mailingZip" required id="mailingZip" label="Zip" fullWidth />}
                        </InputMask>
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <TextField
                          id="mailingCountry"
                          name="mailingCountry"
                          label="Country"
                          helperText="Two-character abbreviation"
                          fullWidth
                          inputProps={{ maxLength: 2 }}
                          value={profile.mailingCountry}
                          onChange={handleChange}
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
              </Fragment>
            </Box>
            <div className={classes.root}>
              <div>
                <Button variant="contained" onClick={handleCancel} className={classes.button}>
                  Cancel
                </Button>
                <Button variant="contained" color="primary" onClick={handleSubmit} className={classes.button} disabled={isSaving}>
                  Save
                  {isSaving && (
                    <Box ml={2} mt={1}>
                      <CircularProgress size={18} color={'inherit'} />
                    </Box>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
        {id !== 'new' && (
          <div className={classes.rootLeft}>
            <div>
              <Button variant="contained" color="secondary" onClick={handleDelete} className={classes.buttonSmall}>
                Delete Profile
              </Button>
            </div>
          </div>
        )}

        <Dialog open={open} onClose={handleClose} aria-labelledby="form-dialog-title">
          <DialogTitle id="form-dialog-title">Are you sure?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              To delete this profile, type in the word <b>delete</b> then click Submit.
            </DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              id="confirm"
              label="type 'delete' to confirm this action"
              type="text"
              fullWidth
              onChange={handleConfirmType}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color="primary">
              Cancel
            </Button>
            <Button onClick={handleConfirmDelete} color="primary">
              Submit
            </Button>
          </DialogActions>
        </Dialog>
      </ApplicationFormContainer>
    );
  } else {
    return <ApplicationFormContainer>Loading...</ApplicationFormContainer>;
  }
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchProfileDelete: (value: MProfile): Promise<unknown> => dispatch(deleteProfileAsync(value)),
  dispatchProfileSave: (value: MProfile, progress: unknown): Promise<unknown> => dispatch(saveProfileAsync(value, progress))
});

export default connect(null, mapDispatchToProps)(ApplicationsFormEditProfile);
